import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageBanner } from "@/components/ui/PageBanner";
import { IMG } from "@/lib/assets";
import { today, addDays, prettyDay, isoDay } from "@/lib/perform";
import { WodEditor } from "@/components/perform/WodParts";
import { useBooking } from "@/components/perform/Timetable";
import { ChevronLeft, ChevronRight, Plus, Sparkles, Check, Trophy, CalendarCheck, Dumbbell, NotebookPen, Loader2 } from "lucide-react";

const TYPE = {
  wod: { label: "Workout", c: "#CCFF00", icon: Dumbbell },
  diary: { label: "Session", c: "#3D7BFF", icon: NotebookPen },
  class: { label: "Class", c: "#B57BFF", icon: CalendarCheck },
  pr: { label: "PR", c: "#FFD60A", icon: Trophy },
};

const localDay = (e) => (e.at ? isoDay(new Date(e.at)) : e.date);
const localTime = (e) => (e.at ? new Date(e.at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : e.time || "");

function monthGrid(anchor) {
  const [y, m] = anchor.split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const start = addDays(isoDay(first), -((first.getDay() + 6) % 7));
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { busy, book, cancel } = useBooking();
  const [anchor, setAnchor] = useState(today().slice(0, 8) + "01");
  const [sel, setSel] = useState(today());
  const [editor, setEditor] = useState(null);
  const [planning, setPlanning] = useState(false);
  const cells = useMemo(() => monthGrid(anchor), [anchor]);
  const { data } = useQuery({ queryKey: ["calendar", cells[0], cells[41]],
    queryFn: async () => (await api.get("/api/calendar", { params: { start: cells[0], end: cells[41] } })).data });
  const byDay = useMemo(() => {
    const m = {};
    (data?.events || []).forEach((e) => { (m[localDay(e)] = m[localDay(e)] || []).push(e); });
    return m;
  }, [data]);
  const month = Number(anchor.slice(5, 7));
  const shiftMonth = (n) => { const [y, mm] = anchor.split("-").map(Number); setAnchor(isoDay(new Date(y, mm - 1 + n, 1))); };
  const refresh = () => qc.invalidateQueries({ queryKey: ["calendar"] });
  const doneDiary = async (e) => {
    try { await api.patch(`/api/diary/${e.id}`, { status: e.done ? "planned" : "done" }); refresh(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const planWeek = async () => {
    setPlanning(true);
    try { await api.post("/api/diary/plan-week", null, { params: { tz_offset: new Date().getTimezoneOffset() } }); toast.success("OCTON planned your week. Sessions are on your calendar."); refresh(); }
    catch (err) { toast.error(apiErr(err)); }
    setPlanning(false);
  };
  const dayEvents = byDay[sel] || [];
  const monthCount = Object.entries(byDay).filter(([d, es]) => Number(d.slice(5, 7)) === month && es.some((e) => e.done || e.type === "pr")).length;
  return (
    <div className="p-4 md:p-8 space-y-6">
      <PageBanner image={IMG.progress} overline="training calendar" title="Your training calendar"
        subtitle="Every workout, class and session in one place. Plan ahead, log what you did and see your PRs land."
        actions={
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setEditor({ date: sel })} data-testid="cal-add" className="inline-flex items-center gap-2 rounded-full h-10 px-5 bg-acid text-black font-medium"><Plus className="w-4 h-4" /> Log or plan</button>
            <button onClick={planWeek} disabled={planning} className="inline-flex items-center gap-2 rounded-full h-10 px-4 border border-white/30 backdrop-blur disabled:opacity-60">
              {planning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} OCTON, plan my week
            </button>
          </div>} />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 surface rounded-2xl p-4 md:p-5">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => shiftMonth(-1)} className="p-2 rounded-full border border-[#343A4A]" aria-label="Previous month"><ChevronLeft className="w-4 h-4" /></button>
            <div className="text-center">
              <p className="font-heading font-black text-xl">{prettyDay(anchor, { month: "long", year: "numeric" })}</p>
              <p className="mono-label text-acid">{monthCount} training days</p>
            </div>
            <button onClick={() => shiftMonth(1)} className="p-2 rounded-full border border-[#343A4A]" aria-label="Next month"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-7 gap-1.5 mb-1.5">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <p key={d} className="mono-label text-center">{d}</p>)}
          </div>
          <div className="grid grid-cols-7 gap-1.5" data-testid="cal-grid">
            {cells.map((d) => {
              const es = byDay[d] || [];
              const inMonth = Number(d.slice(5, 7)) === month;
              const done = es.some((e) => e.done || e.type === "pr");
              const on = d === sel;
              return (
                <button key={d} onClick={() => setSel(d)}
                  className={`relative aspect-square md:aspect-[4/3] rounded-lg border p-1.5 text-left transition-colors ${on ? "border-acid bg-acid/10" : done ? "border-acid/30 bg-[#12160A]" : "border-[#1E222A] bg-[#0B0D11] hover:border-[#343A4A]"} ${inMonth ? "" : "opacity-35"}`}>
                  <span className={`text-xs font-bold ${d === today() ? "bg-acid text-black rounded-full px-1.5" : ""}`}>{Number(d.slice(8))}</span>
                  <div className="absolute bottom-1.5 left-1.5 right-1.5 flex flex-wrap gap-1">
                    {es.filter((e) => !e.bookable).slice(0, 4).map((e, i) => (
                      <span key={i} className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full" style={{ background: TYPE[e.type]?.c, opacity: e.done || e.type === "pr" ? 1 : 0.45 }} />
                    ))}
                  </div>
                  {done && <Check className="absolute top-1.5 right-1.5 w-3 h-3 text-acid" />}
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-4 mt-4">
            {Object.values(TYPE).map((t) => <span key={t.label} className="inline-flex items-center gap-1.5 text-xs text-[#8A91A6]"><span className="w-2 h-2 rounded-full" style={{ background: t.c }} /> {t.label}</span>)}
            <span className="text-xs text-[#565C6F]">faded = planned</span>
          </div>
        </div>
        <div className="lg:col-span-4 space-y-3">
          <div className="surface rounded-2xl p-5" data-testid="cal-agenda">
            <div className="flex items-center justify-between">
              <div>
                <p className="mono-label text-acid">{sel === today() ? "today" : sel < today() ? "past" : "upcoming"}</p>
                <p className="font-heading font-black text-lg">{prettyDay(sel)}</p>
              </div>
              <button onClick={() => setEditor({ date: sel })} className="p-2 rounded-full bg-acid text-black" aria-label="Add"><Plus className="w-4 h-4" /></button>
            </div>
            <div className="mt-4 space-y-2">
              {dayEvents.length === 0 && <p className="text-sm text-[#8A91A6]">Nothing here yet. {sel <= today() ? "Log a workout you did." : "Plan a session."}</p>}
              {dayEvents.map((e) => {
                const t = TYPE[e.type] || TYPE.wod; const I = t.icon;
                return (
                  <motion.div key={`${e.type}-${e.id}`} initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }}
                    className="rounded-xl border border-[#1E222A] bg-[#0B0D11] p-3 flex items-start gap-3">
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${t.c}1F`, color: t.c }}><I className="w-4 h-4" /></span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold truncate">{e.title}</p>
                      <p className="text-xs text-[#8A91A6]">{e.type === "class" ? (e.booked ? (e.done ? "Class · logged" : "Class · booked") : `Class · ${Math.max(0, (e.capacity || 0) - (e.booked_count || 0))} places left`) : t.label}{localTime(e) ? ` · ${localTime(e)}` : ""}{e.scope === "club" ? " · club" : ""}{e.hidden ? " · revealed soon" : ""}</p>
                      {e.scores?.length > 0 && <p className="text-xs text-acid mt-1">{e.scores.map((s) => `${s.section_title}: ${s.display}`).join(" · ")}</p>}
                      {e.detail && <p className="text-xs text-[#FFD60A] mt-1">{e.detail}</p>}
                    </div>
                    {e.type === "class" && e.bookable && (
                      <button onClick={() => book({ ...e, starts_at: e.at })} disabled={busy === e.id || e.booked_count >= e.capacity} data-testid="cal-book"
                        className="text-xs shrink-0 rounded-full px-3 h-7 bg-acid text-black font-semibold disabled:opacity-40">{e.booked_count >= e.capacity ? "Full" : "Book"}</button>
                    )}
                    {e.type === "class" && e.booked && (
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        {e.date <= today() && <button onClick={() => navigate(`/app/today?day=${e.date}`)} data-testid="cal-log" className="text-xs text-acid">{e.done ? "View log" : "Log workout"}</button>}
                        {new Date(e.at) > new Date() && <button onClick={() => cancel({ ...e, starts_at: e.at })} disabled={busy === e.id} className="text-[11px] text-[#8A91A6]">Cancel</button>}
                      </div>
                    )}
                    {e.type === "wod" && <button onClick={() => navigate(`/app/today?day=${e.date}`)} className="text-xs text-acid shrink-0">{e.done ? "View" : "Log"}</button>}
                    {e.type === "diary" && (
                      <button onClick={() => doneDiary(e)} className={`text-xs shrink-0 rounded-full px-2 h-6 border ${e.done ? "border-acid text-acid" : "border-[#343A4A] text-[#8A91A6]"}`}>{e.done ? "Done" : "Mark done"}</button>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>
          <div className="surface rounded-2xl p-5 text-sm text-[#B7BECB]">
            <p className="font-heading font-bold text-white mb-1">Tip</p>
            Tell your OCTON Coach what you did ("5x5 back squat at 90") and it lands here automatically, with any PR marked in gold.
          </div>
        </div>
      </div>
      <WodEditor open={!!editor} onOpenChange={(o) => !o && setEditor(null)} initial={editor} scope="personal"
        onSaved={(w) => { refresh(); if (w?.date && w.date <= today()) navigate(`/app/today?day=${w.date}`); }} />
    </div>
  );
}
